import { useEffect, useState, useRef, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { WeatherBackground } from "./WeatherBackground";
import "./index.css";

interface CurrentWeather {
  temperature_2m: number;
  relative_humidity_2m: number;
  apparent_temperature: number;
  weather_code: number;
  wind_speed_10m: number;
  wind_gusts_10m: number;
  uv_index: number;
}

interface DailyWeather {
  time: string[];
  weather_code: number[];
  temperature_2m_max: number[];
  temperature_2m_min: number[];
  apparent_temperature_max: number[];
  apparent_temperature_min: number[];
  precipitation_sum: number[];
  precipitation_probability_max: number[];
  wind_speed_10m_max: number[];
  wind_gusts_10m_max: number[];
  uv_index_max: number[];
  sunrise: string[];
  sunset: string[];
}

interface WeatherData {
  current: CurrentWeather;
  daily: DailyWeather;
}

interface GeoResult {
  id: number;
  name: string;
  latitude: number;
  longitude: number;
  country: string;
  admin1?: string;
  timezone: string;
}

interface City {
  name: string;
  label: string;
  lat: number;
  lon: number;
  timezone: string;
}

type TempUnit = "fahrenheit" | "celsius";

const DEFAULT_CITY: City = {
  name: "San Francisco",
  label: "San Francisco, California, United States",
  lat: 37.7749,
  lon: -122.4194,
  timezone: "America/Los_Angeles",
};

interface TestCity {
  city: City;
  weatherCode: number;
  temp: number;
  humidity: number;
  wind: number;
  gusts: number;
  uv: number;
}

const TEST_CITIES: TestCity[] = [
  {
    city: { name: "Clear Skies", label: "Clear Skies, Test Region", lat: 90.001, lon: 0.001, timezone: "America/Los_Angeles" },
    weatherCode: 0, temp: 78, humidity: 30, wind: 5, gusts: 10, uv: 7,
  },
  {
    city: { name: "Partly Cloudy", label: "Partly Cloudy, Test Region", lat: 90.002, lon: 0.002, timezone: "America/Los_Angeles" },
    weatherCode: 2, temp: 72, humidity: 45, wind: 8, gusts: 15, uv: 5,
  },
  {
    city: { name: "Overcast City", label: "Overcast City, Test Region", lat: 90.003, lon: 0.003, timezone: "America/Los_Angeles" },
    weatherCode: 3, temp: 62, humidity: 70, wind: 12, gusts: 20, uv: 2,
  },
  {
    city: { name: "Foggy Hollow", label: "Foggy Hollow, Test Region", lat: 90.004, lon: 0.004, timezone: "America/Los_Angeles" },
    weatherCode: 45, temp: 55, humidity: 95, wind: 3, gusts: 6, uv: 1,
  },
  {
    city: { name: "Drizzle Town", label: "Drizzle Town, Test Region", lat: 90.005, lon: 0.005, timezone: "America/Los_Angeles" },
    weatherCode: 55, temp: 58, humidity: 85, wind: 7, gusts: 14, uv: 1,
  },
  {
    city: { name: "Rainy Falls", label: "Rainy Falls, Test Region", lat: 90.006, lon: 0.006, timezone: "America/Los_Angeles" },
    weatherCode: 63, temp: 52, humidity: 88, wind: 15, gusts: 25, uv: 1,
  },
  {
    city: { name: "Heavy Rainfall", label: "Heavy Rainfall, Test Region", lat: 90.007, lon: 0.007, timezone: "America/Los_Angeles" },
    weatherCode: 65, temp: 50, humidity: 92, wind: 20, gusts: 35, uv: 0,
  },
  {
    city: { name: "Snowfall Valley", label: "Snowfall Valley, Test Region", lat: 90.008, lon: 0.008, timezone: "America/Los_Angeles" },
    weatherCode: 75, temp: 28, humidity: 80, wind: 10, gusts: 22, uv: 2,
  },
  {
    city: { name: "Thunderstorm Peak", label: "Thunderstorm Peak, Test Region", lat: 90.009, lon: 0.009, timezone: "America/Los_Angeles" },
    weatherCode: 95, temp: 68, humidity: 90, wind: 30, gusts: 55, uv: 0,
  },
  {
    city: { name: "Frozen Tundra", label: "Frozen Tundra, Test Region", lat: 90.010, lon: 0.010, timezone: "America/Los_Angeles" },
    weatherCode: 75, temp: -4, humidity: 75, wind: 18, gusts: 35, uv: 1,
  },
  {
    city: { name: "Arctic Depths", label: "Arctic Depths, Test Region", lat: 90.011, lon: 0.011, timezone: "America/Los_Angeles" },
    weatherCode: 77, temp: -30, humidity: 65, wind: 25, gusts: 45, uv: 0,
  },
];

function isTestCity(c: City): TestCity | undefined {
  return TEST_CITIES.find((t) => t.city.lat === c.lat && t.city.lon === c.lon);
}

function buildMockWeather(tc: TestCity): WeatherData {
  const now = new Date();
  const todayStr = now.toISOString().slice(0, 10);
  const days: string[] = [];
  for (let i = 0; i < 5; i++) {
    const d = new Date(now);
    d.setDate(d.getDate() + i);
    days.push(d.toISOString().slice(0, 10));
  }
  const sunrise = `${todayStr}T06:30`;
  const sunset = `${todayStr}T18:45`;

  return {
    current: {
      temperature_2m: tc.temp,
      relative_humidity_2m: tc.humidity,
      apparent_temperature: tc.temp - 3,
      weather_code: tc.weatherCode,
      wind_speed_10m: tc.wind,
      wind_gusts_10m: tc.gusts,
      uv_index: tc.uv,
    },
    daily: {
      time: days,
      weather_code: days.map(() => tc.weatherCode),
      temperature_2m_max: days.map((_, i) => tc.temp + 4 - i),
      temperature_2m_min: days.map((_, i) => tc.temp - 8 + i),
      apparent_temperature_max: days.map((_, i) => tc.temp + 2 - i),
      apparent_temperature_min: days.map((_, i) => tc.temp - 10 + i),
      precipitation_sum: days.map(() => tc.weatherCode >= 51 ? 0.5 : 0),
      precipitation_probability_max: days.map(() => tc.weatherCode >= 51 ? 80 : 5),
      wind_speed_10m_max: days.map(() => tc.wind),
      wind_gusts_10m_max: days.map(() => tc.gusts),
      uv_index_max: days.map(() => tc.uv),
      sunrise: days.map((d) => `${d}T06:30`),
      sunset: days.map((d) => `${d}T18:45`),
    },
  };
}

function fToC(f: number): number {
  return (f - 32) * 5 / 9;
}

function mphToKmh(mph: number): number {
  return mph * 1.60934;
}

function inToMm(inches: number): number {
  return inches * 25.4;
}

const WMO_CODES: Record<number, { label: string; icon: string }> = {
  0: { label: "Clear sky", icon: "sun" },
  1: { label: "Mainly clear", icon: "sun" },
  2: { label: "Partly cloudy", icon: "cloud-sun" },
  3: { label: "Overcast", icon: "cloud" },
  45: { label: "Fog", icon: "cloud-fog" },
  48: { label: "Depositing rime fog", icon: "cloud-fog" },
  51: { label: "Light drizzle", icon: "cloud-drizzle" },
  53: { label: "Moderate drizzle", icon: "cloud-drizzle" },
  55: { label: "Dense drizzle", icon: "cloud-drizzle" },
  61: { label: "Slight rain", icon: "cloud-rain" },
  63: { label: "Moderate rain", icon: "cloud-rain" },
  65: { label: "Heavy rain", icon: "cloud-rain" },
  66: { label: "Light freezing rain", icon: "cloud-rain" },
  67: { label: "Heavy freezing rain", icon: "cloud-rain" },
  71: { label: "Slight snow", icon: "snowflake" },
  73: { label: "Moderate snow", icon: "snowflake" },
  75: { label: "Heavy snow", icon: "snowflake" },
  77: { label: "Snow grains", icon: "snowflake" },
  80: { label: "Slight showers", icon: "cloud-rain" },
  81: { label: "Moderate showers", icon: "cloud-rain" },
  82: { label: "Violent showers", icon: "cloud-rain" },
  85: { label: "Slight snow showers", icon: "snowflake" },
  86: { label: "Heavy snow showers", icon: "snowflake" },
  95: { label: "Thunderstorm", icon: "cloud-lightning" },
  96: { label: "Thunderstorm w/ slight hail", icon: "cloud-lightning" },
  99: { label: "Thunderstorm w/ heavy hail", icon: "cloud-lightning" },
};

function getWeatherInfo(code: number) {
  return WMO_CODES[code] ?? { label: "Unknown", icon: "cloud" };
}

function WeatherIcon({ type, size = 48 }: { type: string; size?: number }) {
  const s = size;
  const stroke = size < 40 ? 2 : 1.5;

  switch (type) {
    case "sun":
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" className="text-amber-400">
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
        </svg>
      );
    case "cloud-sun":
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" className="text-sky-400">
          <path d="M12 2v2M4.93 4.93l1.41 1.41M20 12h2M2 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
          <circle cx="12" cy="8" r="3" className="text-amber-400" />
          <path d="M17 18a5 5 0 0 0-10 0h10z" className="text-slate-300" />
          <path d="M7 18H5a3 3 0 0 1 0-6h.5" className="text-slate-300" />
          <path d="M19 18a3 3 0 0 0 0-6h-.5" className="text-slate-300" />
        </svg>
      );
    case "cloud":
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" className="text-slate-400">
          <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" />
        </svg>
      );
    case "cloud-fog":
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" className="text-slate-400">
          <path d="M17.5 15H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" />
          <path d="M6 19h12M8 22h8" />
        </svg>
      );
    case "cloud-drizzle":
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" className="text-blue-400">
          <path d="M17.5 13H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" className="text-slate-400" />
          <path d="M8 19v1M8 15v1M12 19v1M12 15v1M16 19v1M16 15v1" />
        </svg>
      );
    case "cloud-rain":
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" className="text-blue-500">
          <path d="M17.5 13H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" className="text-slate-400" />
          <path d="M8 15v4M12 15v4M16 15v4" />
        </svg>
      );
    case "snowflake":
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" className="text-sky-300">
          <path d="M12 2v20M17 7l-10 10M2 12h20M7 7l10 10M7 17L12 12 17 7M7 7l5 5 5-5" />
        </svg>
      );
    case "cloud-lightning":
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" className="text-yellow-500">
          <path d="M17.5 13H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" className="text-slate-500" />
          <path d="M13 15l-2 5h4l-2 5" />
        </svg>
      );
    default:
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" className="text-slate-400">
          <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" />
        </svg>
      );
  }
}

function formatDay(dateStr: string) {
  const date = new Date(dateStr + "T12:00:00");
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (date.toDateString() === today.toDateString()) return "Today";
  if (date.toDateString() === tomorrow.toDateString()) return "Tomorrow";

  return date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

function formatTime(isoStr: string) {
  const date = new Date(isoStr);
  return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

function UVBadge({ value }: { value: number }) {
  let color = "bg-green-500/20 text-green-400";
  let label = "Low";
  if (value >= 3 && value < 6) { color = "bg-yellow-500/20 text-yellow-400"; label = "Moderate"; }
  else if (value >= 6 && value < 8) { color = "bg-orange-500/20 text-orange-400"; label = "High"; }
  else if (value >= 8 && value < 11) { color = "bg-red-500/20 text-red-400"; label = "Very High"; }
  else if (value >= 11) { color = "bg-purple-500/20 text-purple-400"; label = "Extreme"; }

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
      UV {Math.round(value)} - {label}
    </span>
  );
}

function UnitToggle({ unit, onToggle }: { unit: TempUnit; onToggle: (u: TempUnit) => void }) {
  return (
    <div className="flex items-center bg-slate-800/80 border border-slate-700/50 rounded-lg shrink-0">
      <button
        onClick={() => onToggle("fahrenheit")}
        className={`px-3 py-2 text-sm font-medium transition-colors rounded-l-lg ${
          unit === "fahrenheit"
            ? "bg-sky-500/20 text-sky-400"
            : "text-slate-500 hover:text-slate-300"
        }`}
      >
        °F
      </button>
      <div className="w-px h-5 bg-slate-700/50" />
      <button
        onClick={() => onToggle("celsius")}
        className={`px-3 py-2 text-sm font-medium transition-colors rounded-r-lg ${
          unit === "celsius"
            ? "bg-sky-500/20 text-sky-400"
            : "text-slate-500 hover:text-slate-300"
        }`}
      >
        °C
      </button>
    </div>
  );
}

type SearchItem = { kind: "geo"; data: GeoResult } | { kind: "test"; data: TestCity };

function CitySearch({ city, onCityChange }: { city: City; onCityChange: (city: City) => void }) {
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<SearchItem[]>([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const search = useCallback((q: string) => {
    const trimmed = q.trim().toLowerCase();
    if (trimmed.length < 2) {
      setItems([]);
      return;
    }

    const matchingTest: SearchItem[] = TEST_CITIES
      .filter((t) => t.city.name.toLowerCase().includes(trimmed) || t.city.label.toLowerCase().includes(trimmed))
      .map((t) => ({ kind: "test", data: t }));

    fetch(`/api/geocode?q=${encodeURIComponent(q)}`)
      .then((r) => r.json())
      .then((data) => {
        const geoItems: SearchItem[] = (data.results || []).map((r: GeoResult) => ({ kind: "geo", data: r }));
        setItems([...matchingTest, ...geoItems]);
        setOpen(true);
        setActiveIndex(-1);
      })
      .catch(() => {
        setItems(matchingTest);
        if (matchingTest.length > 0) {
          setOpen(true);
          setActiveIndex(-1);
        }
      });
  }, []);

  const handleInput = (value: string) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(value), 300);
  };

  const selectItem = (item: SearchItem) => {
    if (item.kind === "test") {
      onCityChange(item.data.city);
    } else {
      const r = item.data;
      const parts = [r.name];
      if (r.admin1) parts.push(r.admin1);
      parts.push(r.country);
      onCityChange({
        name: r.name,
        label: parts.join(", "),
        lat: r.latitude,
        lon: r.longitude,
        timezone: r.timezone,
      });
    }
    setQuery("");
    setItems([]);
    setOpen(false);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open || items.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i < items.length - 1 ? i + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i > 0 ? i - 1 : items.length - 1));
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      selectItem(items[activeIndex]);
    } else if (e.key === "Escape") {
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const formatItem = (item: SearchItem): string => {
    if (item.kind === "test") return item.data.city.label;
    const r = item.data;
    const parts = [r.name];
    if (r.admin1) parts.push(r.admin1);
    parts.push(r.country);
    return parts.join(", ");
  };

  const itemKey = (item: SearchItem, i: number): string => {
    if (item.kind === "test") return `test-${item.data.city.lat}`;
    return `geo-${item.data.id}`;
  };

  return (
    <div ref={containerRef} className="relative w-full max-w-md">
      <div className="relative">
        <svg
          className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none"
          width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        >
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.3-4.3" />
        </svg>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => handleInput(e.target.value)}
          onFocus={() => { if (items.length > 0) setOpen(true); }}
          onKeyDown={handleKeyDown}
          placeholder="Search for a city..."
          className="w-full pl-10 pr-4 py-2 bg-slate-800/80 border border-slate-700/50 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500/50 focus:border-sky-500/50 transition-all"
        />
      </div>

      {open && items.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-slate-800 border border-slate-700/50 rounded-lg shadow-xl overflow-hidden">
          {items.map((item, i) => (
            <button
              key={itemKey(item, i)}
              onClick={() => selectItem(item)}
              onMouseEnter={() => setActiveIndex(i)}
              className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 transition-colors ${
                i === activeIndex ? "bg-sky-500/20 text-white" : "text-slate-300 hover:bg-slate-700/50"
              }`}
            >
              {item.kind === "test" ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-amber-500 shrink-0">
                  <path d="M12 2L2 7l10 5 10-5-10-5z" />
                  <path d="M2 17l10 5 10-5" />
                  <path d="M2 12l10 5 10-5" />
                </svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-slate-500 shrink-0">
                  <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
              )}
              <span className="flex items-center gap-2">
                {formatItem(item)}
                {item.kind === "test" && (
                  <span className="text-[10px] uppercase tracking-wider text-amber-500/70 bg-amber-500/10 px-1.5 py-0.5 rounded font-medium">test</span>
                )}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function getFrostLevel(tempCelsius: number): number {
  if (tempCelsius >= 0) return 0;
  if (tempCelsius >= -5) return 1;
  if (tempCelsius >= -15) return 2;
  if (tempCelsius >= -25) return 3;
  return 4;
}

function FrostOverlay({ level }: { level: number }) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const blurRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<number>(0);
  const mouseRef = useRef<{ x: number; y: number; active: boolean }>({ x: -999, y: -999, active: false });
  const meltMapRef = useRef<Float32Array | null>(null);
  const maskCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const hasMeltedRef = useRef(false);

  useEffect(() => {
    if (level === 0) return;
    const wrapper = wrapperRef.current;
    const canvas = canvasRef.current;
    const blurDiv = blurRef.current;
    if (!wrapper || !canvas || !blurDiv) return;

    const resizeCanvas = () => {
      const rect = wrapper.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      canvas.style.width = rect.width + "px";
      canvas.style.height = rect.height + "px";
    };
    resizeCanvas();

    const ctx = canvas.getContext("2d")!;
    const mw = 64;
    const mh = 64;

    // Offscreen canvas for the melt mask
    if (!maskCanvasRef.current) {
      maskCanvasRef.current = document.createElement("canvas");
      maskCanvasRef.current.width = mw;
      maskCanvasRef.current.height = mh;
    }
    const maskCanvas = maskCanvasRef.current;
    const maskCtx = maskCanvas.getContext("2d")!;

    if (!meltMapRef.current || meltMapRef.current.length !== mw * mh) {
      meltMapRef.current = new Float32Array(mw * mh);
      meltMapRef.current.fill(1);
    }
    const meltMap = meltMapRef.current;

    const meltRadius = 10 + level * 2;
    const meltStrength = 0.09 + level * 0.015;
    const refreezeRate = 0.003 + level * 0.0015;

    const iceOpacity = Math.min(0.25 + level * 0.08, 0.55);
    const rimOpacity = Math.min(0.15 + level * 0.1, 0.5);

    const draw = () => {
      const cw = canvas.width;
      const ch = canvas.height;
      if (cw === 0 || ch === 0) { animRef.current = requestAnimationFrame(draw); return; }

      const rect = wrapper.getBoundingClientRect();
      const mouse = mouseRef.current;

      // Update melt map
      if (mouse.active) {
        const mx = ((mouse.x - rect.left) / rect.width) * mw;
        const my = ((mouse.y - rect.top) / rect.height) * mh;
        const xMin = Math.max(0, Math.floor(mx - meltRadius));
        const xMax = Math.min(mw - 1, Math.ceil(mx + meltRadius));
        const yMin = Math.max(0, Math.floor(my - meltRadius));
        const yMax = Math.min(mh - 1, Math.ceil(my + meltRadius));

        for (let y = yMin; y <= yMax; y++) {
          for (let x = xMin; x <= xMax; x++) {
            const dx = x - mx;
            const dy = y - my;
            const d2 = dx * dx + dy * dy;
            const r2 = meltRadius * meltRadius;
            if (d2 < r2) {
              const factor = 1 - Math.sqrt(d2) / meltRadius;
              meltMap[y * mw + x] = Math.max(0, meltMap[y * mw + x] - meltStrength * factor * factor);
            }
          }
        }
        hasMeltedRef.current = true;
      }

      // Refreeze
      if (hasMeltedRef.current) {
        let allFrozen = true;
        for (let i = 0; i < mw * mh; i++) {
          if (meltMap[i] < 1) {
            meltMap[i] = Math.min(1, meltMap[i] + refreezeRate);
            allFrozen = false;
          }
        }
        if (allFrozen) hasMeltedRef.current = false;
      }

      // Build mask data URL and apply to both blur div and canvas
      const maskImageData = maskCtx.createImageData(mw, mh);
      for (let i = 0; i < mw * mh; i++) {
        const v = Math.round(meltMap[i] * 255);
        maskImageData.data[i * 4] = v;
        maskImageData.data[i * 4 + 1] = v;
        maskImageData.data[i * 4 + 2] = v;
        maskImageData.data[i * 4 + 3] = 255;
      }
      maskCtx.putImageData(maskImageData, 0, 0);
      const maskUrl = `url(${maskCanvas.toDataURL()})`;
      blurDiv.style.maskImage = maskUrl;
      blurDiv.style.maskSize = "100% 100%";
      blurDiv.style.WebkitMaskImage = maskUrl;
      blurDiv.style.WebkitMaskSize = "100% 100%";
      canvas.style.maskImage = maskUrl;
      canvas.style.maskSize = "100% 100%";
      canvas.style.WebkitMaskImage = maskUrl;
      canvas.style.WebkitMaskSize = "100% 100%";

      // Draw ice texture on canvas
      ctx.clearRect(0, 0, cw, ch);

      // Ice base tint
      ctx.fillStyle = `rgba(195, 220, 245, ${iceOpacity})`;
      ctx.fillRect(0, 0, cw, ch);

      // Frost edge gradient - top
      const topH = ch * (0.2 + level * 0.1);
      const topGrad = ctx.createLinearGradient(0, 0, 0, topH);
      topGrad.addColorStop(0, `rgba(220, 240, 255, ${Math.min(iceOpacity + 0.1, 0.6)})`);
      topGrad.addColorStop(1, "rgba(220, 240, 255, 0)");
      ctx.fillStyle = topGrad;
      ctx.fillRect(0, 0, cw, topH);

      // Frost edge gradient - bottom
      const botH = ch * (0.15 + level * 0.08);
      const botGrad = ctx.createLinearGradient(0, ch, 0, ch - botH);
      botGrad.addColorStop(0, `rgba(220, 240, 255, ${Math.min(iceOpacity + 0.08, 0.55)})`);
      botGrad.addColorStop(1, "rgba(220, 240, 255, 0)");
      ctx.fillStyle = botGrad;
      ctx.fillRect(0, ch - botH, cw, botH);

      // Ice sheet patches
      const drawPatch = (cx: number, cy: number, rx: number, ry: number, op: number) => {
        const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.max(rx, ry));
        grad.addColorStop(0, `rgba(210, 235, 255, ${op})`);
        grad.addColorStop(1, "rgba(210, 235, 255, 0)");
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
        ctx.fill();
      };
      const sheetOp = Math.min(0.15 + level * 0.08, 0.4);
      drawPatch(cw * 0.25, ch * 0.25, cw * 0.35, ch * 0.35, sheetOp);
      drawPatch(cw * 0.75, ch * 0.75, cw * 0.35, ch * 0.35, sheetOp * 0.9);
      drawPatch(cw * 0.6, ch * 0.3, cw * 0.4, ch * 0.4, sheetOp * 0.5);

      // Ice rim glow
      const rimGrad = ctx.createLinearGradient(0, 0, 0, ch);
      rimGrad.addColorStop(0, `rgba(170, 210, 250, ${rimOpacity * 1.5})`);
      rimGrad.addColorStop(0.15, "rgba(170, 210, 250, 0)");
      rimGrad.addColorStop(0.85, "rgba(170, 210, 250, 0)");
      rimGrad.addColorStop(1, `rgba(170, 210, 250, ${rimOpacity * 1.5})`);
      ctx.fillStyle = rimGrad;
      ctx.fillRect(0, 0, cw, ch);
      const rimGradH = ctx.createLinearGradient(0, 0, cw, 0);
      rimGradH.addColorStop(0, `rgba(170, 210, 250, ${rimOpacity * 1.2})`);
      rimGradH.addColorStop(0.15, "rgba(170, 210, 250, 0)");
      rimGradH.addColorStop(0.85, "rgba(170, 210, 250, 0)");
      rimGradH.addColorStop(1, `rgba(170, 210, 250, ${rimOpacity * 1.2})`);
      ctx.fillStyle = rimGradH;
      ctx.fillRect(0, 0, cw, ch);

      // Frozen border
      ctx.strokeStyle = `rgba(200, 230, 255, ${Math.min(0.2 + level * 0.12, 0.6)})`;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.roundRect(0.5, 0.5, cw - 1, ch - 1, 10);
      ctx.stroke();

      animRef.current = requestAnimationFrame(draw);
    };

    const ro = new ResizeObserver(resizeCanvas);
    ro.observe(wrapper);

    animRef.current = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(animRef.current);
      ro.disconnect();
    };
  }, [level]);

  if (level === 0) return null;

  const blurAmount = 3 + level * 3;

  return (
    <div
      ref={wrapperRef}
      onMouseMove={(e) => { mouseRef.current = { x: e.clientX, y: e.clientY, active: true }; }}
      onMouseLeave={() => { mouseRef.current = { ...mouseRef.current, active: false }; }}
      style={{
        position: "absolute", inset: 0, overflow: "hidden",
        borderRadius: "inherit", zIndex: 15, cursor: "default",
      }}
    >
      {/* Backdrop blur layer — blurs the content beneath, masked by melt map */}
      <div
        ref={blurRef}
        style={{
          position: "absolute", inset: 0,
          backdropFilter: `blur(${blurAmount}px)`,
          WebkitBackdropFilter: `blur(${blurAmount}px)`,
          borderRadius: "inherit",
        }}
      />
      {/* Ice texture canvas — color, gradients, rim glow, border, masked by melt map */}
      <canvas
        ref={canvasRef}
        style={{
          position: "absolute", inset: 0,
          borderRadius: "inherit",
          pointerEvents: "none",
        }}
      />
    </div>
  );
}

function CurrentWeatherCard({ current, unit }: { current: CurrentWeather; unit: TempUnit }) {
  const info = getWeatherInfo(current.weather_code);
  const isCelsius = unit === "celsius";
  const temp = isCelsius ? fToC(current.temperature_2m) : current.temperature_2m;
  const feelsLike = isCelsius ? fToC(current.apparent_temperature) : current.apparent_temperature;
  const wind = isCelsius ? mphToKmh(current.wind_speed_10m) : current.wind_speed_10m;
  const tempLabel = isCelsius ? "°C" : "°F";
  const windLabel = isCelsius ? "km/h" : "mph";
  const tempC = fToC(current.temperature_2m);
  const frostLevel = getFrostLevel(tempC);
  return (
    <Card data-weather-surface className={`relative overflow-hidden bg-gradient-to-br from-slate-800/80 to-slate-900/80 border-slate-700/50 backdrop-blur-lg ${frostLevel > 0 ? "frost-card" : ""}`}>
      <FrostOverlay level={frostLevel} />
      <CardHeader className="pb-2 relative z-10">
        <CardTitle className="text-slate-400 text-sm font-medium uppercase tracking-wider">Current Weather</CardTitle>
      </CardHeader>
      <CardContent className="relative z-10">
        <div className="flex items-center gap-6">
          <WeatherIcon type={info.icon} size={72} />
          <div>
            <div className="text-6xl font-bold text-white tracking-tight">
              {Math.round(temp)}°
            </div>
            <div className="text-slate-300 text-lg mt-1">{info.label}</div>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6 pt-4 border-t border-slate-700/50">
          <div>
            <div className="text-slate-500 text-xs uppercase tracking-wider">Feels Like</div>
            <div className="text-white text-lg font-semibold">{Math.round(feelsLike)}{tempLabel}</div>
          </div>
          <div>
            <div className="text-slate-500 text-xs uppercase tracking-wider">Humidity</div>
            <div className="text-white text-lg font-semibold">{current.relative_humidity_2m}%</div>
          </div>
          <div>
            <div className="text-slate-500 text-xs uppercase tracking-wider">Wind</div>
            <div className="text-white text-lg font-semibold">{Math.round(wind)} {windLabel}</div>
          </div>
          <div>
            <div className="text-slate-500 text-xs uppercase tracking-wider">UV Index</div>
            <div className="mt-1"><UVBadge value={current.uv_index} /></div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function DayCard({ daily, index, unit }: { daily: DailyWeather; index: number; unit: TempUnit }) {
  const info = getWeatherInfo(daily.weather_code[index]);
  const isToday = index === 0;
  const isCelsius = unit === "celsius";
  const maxTemp = isCelsius ? fToC(daily.temperature_2m_max[index]) : daily.temperature_2m_max[index];
  const minTemp = isCelsius ? fToC(daily.temperature_2m_min[index]) : daily.temperature_2m_min[index];
  const wind = isCelsius ? mphToKmh(daily.wind_speed_10m_max[index]) : daily.wind_speed_10m_max[index];
  const gusts = isCelsius ? mphToKmh(daily.wind_gusts_10m_max[index]) : daily.wind_gusts_10m_max[index];
  const precip = isCelsius ? inToMm(daily.precipitation_sum[index]) : daily.precipitation_sum[index];
  const windLabel = isCelsius ? "km/h" : "mph";
  const precipUnit = isCelsius ? " mm" : '"';
  const avgTempC = fToC((daily.temperature_2m_max[index] + daily.temperature_2m_min[index]) / 2);
  const frostLevel = getFrostLevel(avgTempC);

  return (
    <Card data-weather-surface className={`relative overflow-hidden bg-slate-800/60 border-slate-700/40 backdrop-blur-sm transition-all hover:bg-slate-800/80 hover:border-slate-600/50 ${isToday ? "ring-1 ring-sky-500/30" : ""} ${frostLevel > 0 ? "frost-card" : ""}`}>
      <FrostOverlay level={frostLevel} />
      <CardContent className="p-4 relative z-10">
        <div className="flex items-center justify-between mb-3">
          <span className={`text-sm font-semibold ${isToday ? "text-sky-400" : "text-slate-300"}`}>
            {formatDay(daily.time[index])}
          </span>
          <WeatherIcon type={info.icon} size={32} />
        </div>

        <div className="text-xs text-slate-400 mb-3">{info.label}</div>

        <div className="flex items-end gap-1 mb-4">
          <span className="text-2xl font-bold text-white">{Math.round(maxTemp)}°</span>
          <span className="text-lg text-slate-500 mb-0.5">/ {Math.round(minTemp)}°</span>
        </div>

        <div className="space-y-2 text-xs">
          <div className="flex justify-between">
            <span className="text-slate-500">Precip</span>
            <span className="text-slate-300">
              {daily.precipitation_probability_max[index]}%
              {precip > 0 && ` (${precip.toFixed(isCelsius ? 1 : 2)}${precipUnit})`}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Wind</span>
            <span className="text-slate-300">{Math.round(wind)} {windLabel}</span>
          </div>
          {daily.wind_gusts_10m_max[index] > daily.wind_speed_10m_max[index] + 10 && (
            <div className="flex justify-between">
              <span className="text-slate-500">Gusts</span>
              <span className="text-slate-300">{Math.round(gusts)} {windLabel}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-slate-500">UV</span>
            <span><UVBadge value={daily.uv_index_max[index]} /></span>
          </div>
          <div className="flex justify-between pt-1 border-t border-slate-700/30">
            <span className="text-slate-500">Sunrise</span>
            <span className="text-slate-300">{formatTime(daily.sunrise[index])}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Sunset</span>
            <span className="text-slate-300">{formatTime(daily.sunset[index])}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function loadCity(): City {
  try {
    const stored = localStorage.getItem("weather_city");
    if (stored) return JSON.parse(stored);
  } catch {}
  return DEFAULT_CITY;
}

function loadUnit(): TempUnit {
  try {
    const stored = localStorage.getItem("weather_unit");
    if (stored === "celsius" || stored === "fahrenheit") return stored;
  } catch {}
  return "fahrenheit";
}

export function App() {
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [city, setCity] = useState<City>(loadCity);
  const [unit, setUnit] = useState<TempUnit>(loadUnit);

  const fetchWeather = useCallback((c: City) => {
    setLoading(true);
    setError(null);

    const tc = isTestCity(c);
    if (tc) {
      setWeather(buildMockWeather(tc));
      setLoading(false);
      return;
    }

    fetch(`/api/weather?lat=${c.lat}&lon=${c.lon}&timezone=${encodeURIComponent(c.timezone)}`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch weather");
        return res.json();
      })
      .then((data) => {
        setWeather(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchWeather(city);
  }, [city, fetchWeather]);

  const handleCityChange = (newCity: City) => {
    setCity(newCity);
    try { localStorage.setItem("weather_city", JSON.stringify(newCity)); } catch {}
  };

  const handleUnitChange = (newUnit: TempUnit) => {
    setUnit(newUnit);
    try { localStorage.setItem("weather_unit", newUnit); } catch {}
  };

  const header = (
    <header className="mb-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
        <div>
          <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            {city.label}
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white">5-Day Forecast</h1>
        </div>
        <div className="flex items-center gap-3">
          <UnitToggle unit={unit} onToggle={handleUnitChange} />
          <CitySearch city={city} onCityChange={handleCityChange} />
        </div>
      </div>
    </header>
  );

  const weatherCode = weather?.current?.weather_code ?? 0;
  const todaySunrise = weather?.daily?.sunrise?.[0];
  const todaySunset = weather?.daily?.sunset?.[0];
  const tz = city.timezone;

  if (loading) {
    return (
      <>
        <WeatherBackground weatherCode={weatherCode} sunrise={todaySunrise} sunset={todaySunset} timezone={tz} />
        <div className="relative z-10 min-h-screen w-full p-4 sm:p-8">
          <div className="max-w-5xl mx-auto">
            {header}
            <div className="flex items-center justify-center py-20">
              <div className="flex flex-col items-center gap-3">
                <div className="w-10 h-10 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
                <span className="text-slate-400 text-sm">Loading weather data...</span>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (error || !weather) {
    return (
      <>
        <WeatherBackground weatherCode={weatherCode} sunrise={todaySunrise} sunset={todaySunset} timezone={tz} />
        <div className="relative z-10 min-h-screen w-full p-4 sm:p-8">
          <div className="max-w-5xl mx-auto">
            {header}
            <Card className="bg-red-900/20 border-red-800/50">
              <CardContent className="p-6 text-center">
                <div className="text-red-400 text-lg font-semibold">Failed to load weather</div>
                <div className="text-red-400/70 text-sm mt-1">{error}</div>
                <button
                  onClick={() => fetchWeather(city)}
                  className="mt-4 px-4 py-2 bg-red-500/20 text-red-300 rounded-lg hover:bg-red-500/30 transition-colors text-sm"
                >
                  Retry
                </button>
              </CardContent>
            </Card>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <WeatherBackground weatherCode={weatherCode} sunrise={todaySunrise} sunset={todaySunset} timezone={tz} />
      <div className="relative z-10 min-h-screen w-full p-4 sm:p-8">
        <div className="max-w-5xl mx-auto">
          {header}

          <div className="mb-8">
            <CurrentWeatherCard current={weather.current} unit={unit} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {weather.daily.time.map((_, i) => (
              <DayCard key={weather.daily.time[i]} daily={weather.daily} index={i} unit={unit} />
            ))}
          </div>

          <footer className="mt-8 text-center text-slate-600 text-xs">
            Data from <a href="https://open-meteo.com/" className="underline hover:text-slate-400 transition-colors" target="_blank" rel="noopener noreferrer">Open-Meteo</a>
          </footer>
        </div>
      </div>
    </>
  );
}

export default App;
